/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  Category,
  ListAIDetectionsRequest,
  ListAIDetectionsSecurity,
} from "../models/operations/listaidetections.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  AiDetectionsQueryData,
  buildAiDetectionsQuery,
  prefetchAiDetections,
  queryKeyAiDetections,
} from "./aiDetections.core.js";
export {
  type AiDetectionsQueryData,
  buildAiDetectionsQuery,
  prefetchAiDetections,
  queryKeyAiDetections,
};

export type AiDetectionsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listAIDetections access
 *
 * @remarks
 * List AI tools detected on enrolled devices by device-agent AI scans, aggregated per detection target across the organization. Org-scoped — detections attach to devices and enrolled users, not projects. Requires an authenticated session authorized for org:admin on the active organization. Each row carries the organization's gateway access decision for that tool. Display names and categories are decorated from the server's detection target catalog at read time; targets the catalog does not know are listed under their raw reported id.
 */
export function useAiDetections(
  request?: ListAIDetectionsRequest | undefined,
  security?: ListAIDetectionsSecurity | undefined,
  options?: QueryHookOptions<AiDetectionsQueryData, AiDetectionsQueryError>,
): UseQueryResult<AiDetectionsQueryData, AiDetectionsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildAiDetectionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listAIDetections access
 *
 * @remarks
 * List AI tools detected on enrolled devices by device-agent AI scans, aggregated per detection target across the organization. Org-scoped — detections attach to devices and enrolled users, not projects. Requires an authenticated session authorized for org:admin on the active organization. Each row carries the organization's gateway access decision for that tool. Display names and categories are decorated from the server's detection target catalog at read time; targets the catalog does not know are listed under their raw reported id.
 */
export function useAiDetectionsSuspense(
  request?: ListAIDetectionsRequest | undefined,
  security?: ListAIDetectionsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    AiDetectionsQueryData,
    AiDetectionsQueryError
  >,
): UseSuspenseQueryResult<AiDetectionsQueryData, AiDetectionsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildAiDetectionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setAiDetectionsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      category?: Category | undefined;
      directoryGroupId?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: AiDetectionsQueryData,
): AiDetectionsQueryData | undefined {
  const key = queryKeyAiDetections(...queryKeyBase);

  return client.setQueryData<AiDetectionsQueryData>(key, data);
}

export function invalidateAiDetections(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      category?: Category | undefined;
      directoryGroupId?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "listAIDetections", ...queryKeyBase],
  });
}

export function invalidateAllAiDetections(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "listAIDetections"],
  });
}
